import { journalBoundaryContext, boundaryValue } from '../ability/boundary-journal.js';
import { type JanusCtx, type Row, HandBack, Refusal, activeStatuses, ec, ecId, ecLean, handBack, insertRoll, itemField, record, requiredInt, scalar, upper } from '../ability/context.js';
import { janus } from '../ability/janus.js';
import { commitShapeGate } from '../actions/ability-use.js';
import { boundaryCheckpoint, releaseRollback } from '../ability/boundary-checkpoint.js';
import { statusScalar } from '../actions/activity.js';
import { statusItemId, removeStatusFromRow } from '../statuses/expiry.js';
import { isSelfStatusOrigin } from '../statuses/status-origin.js';
import { calculateVitalityStatus, hydrateVitality, VITALITY_STATUS_IDS } from '../compile/vitality-reader.js';
import { CONTINGENCY_ARMING_IDS } from './sanguine-gate.js';
import { unportedSkipRow } from '../unported.js';
import { phpIntval as intval } from '../rolls/dice-roll.js';
import { manaReductionDelta, type ReductionReaders } from '../resources/reduction-guard.js';
import { resourceReductionReaders, setMana } from '../ability/resource-reduction.js';
import { frameCopyOnRead } from '../ability/frame-snapshot.js';

/** The stored JSON pack and the legacy cond|mana|level shape share one reader. */
export function contingencyParse(packed: unknown): { cond: string; mana: number; level: number } {
  const s = scalar(packed);
  if (typeof s !== 'string' || s === '') return { cond: '', mana: 0, level: 0 };
  if (s[0] === '{') {
    try {
      const o = record(JSON.parse(s));
      if (o && 'c' in o) { const mana = Math.max(0, intval(o.m ?? 0)); return { cond: String(o.c), mana, level: 'l' in o ? Math.max(0, intval(o.l)) : mana }; }
    } catch { /* unreadable arm */ }
    return { cond: '', mana: 0, level: 0 };
  }
  const parts = s.split('|'), mana = Math.max(0, intval(parts[1] ?? 0));
  return { cond: parts[0] ?? '', mana, level: parts.length > 2 ? Math.max(0, intval(parts.slice(2).join('|'))) : mana };
}

type ManaChange = { before: number; after: number } | null;
/** Shared combat/damage policy: refund a fizzle or pay only a reauthored spell's shortfall. */
export function contingencyManaPlan(parsed: ReturnType<typeof contingencyParse>, spell: Row, level: number, mana: number, total: unknown,
  protection?: { holder: Row; read: ReductionReaders }):
  { kind: 'fizzle'; reason: string; mana: ManaChange } | { kind: 'fire'; castLevel: number; mana: ManaChange } {
  const refund = (): ManaChange => {
    if (parsed.mana <= 0) return null;
    const cap = scalar(total), numeric = typeof cap === 'number' ? Number.isFinite(cap) : typeof cap === 'string' && cap.trim() !== '' && Number.isFinite(Number(cap));
    const after = numeric ? Math.min(mana + parsed.mana, intval(cap)) : mana + parsed.mana;
    return after > mana ? { before: mana, after } : null;
  };
  if (['item_deleted', 'item_private', 'item_archived'].some(k => intval(itemField(spell, k) ?? 0) === 1))
    return { kind: 'fizzle', reason: 'spell-unavailable', mana: refund() };
  const requested = level > parsed.level ? Math.max(0, level - parsed.mana) : 0;
  const shortfall = protection ? -manaReductionDelta(protection.holder, -requested, protection.read) : requested;
  if (shortfall > mana) return { kind: 'fizzle', reason: 'spell-grew', mana: refund() };
  return { kind: 'fire', castLevel: Math.max(level, parsed.level), mana: shortfall > 0 ? { before: mana, after: mana - shortfall } : null };
}

export interface ContingencyCast { ownerId: string; spellId: string; castLevel: number | null; targetIds?: string[] }
/** Resolve eligibility and all stored identities before claiming the arm or writing a log/resource. */
function contingencyCastPlan(c: JanusCtx, fire: ContingencyCast, heldSpell?: Row) {
  const ownerId = upper(fire.ownerId), spellId = upper(fire.spellId), caster = c.members.get(ownerId);
  if (!caster) handBack('engine-fired:owner-not-held');
  const spell = heldSpell ?? c.read.definition(spellId);
  if (!spell) handBack('contingency:spell-not-held');
  if (['item_deleted', 'item_private', 'item_archived'].some(k => Number(itemField(spell, k) ?? 0) === 1)) handBack('contingency:spell-unavailable');
  const level = c.read.has(spell, 'spell_level') ? requiredInt(c.read.value(spell, 'spell_level'), 'contingency:invalid-level') : 0;
  if (fire.castLevel !== null && level > fire.castLevel) handBack('contingency:level-grew');
  if (fire.castLevel !== null && fire.castLevel > level) handBack('contingency:upcast-not-modelled');
  const targetIds = fire.targetIds ?? [ownerId], targets = targetIds.map(id => c.members.get(upper(id)));
  if (!targets.length || targets.some(row => !row)) handBack('contingency:stored-target-not-held');
  return { ownerId, spellId, caster, spell, targets: targets as Row[], targetIds };
}

function applyContingencyCast(c: JanusCtx, plan: ReturnType<typeof contingencyCastPlan>): void {
  const { ownerId, spellId, caster, spell, targets, targetIds } = plan;
  insertRoll(c, { roll_type_1: 'contingency_triggered', roll_item_id: spellId, by: caster, to: targets[0]!, logMessage: true });
  const prior = { trigger: c.trigger, engine: c.engineFiredCast, connection: c.statusConnectedId };
  c.trigger = 'contingency'; c.engineFiredCast = { casterId: ownerId, abilityId: spellId }; c.statusConnectedId = null;
  try {
    const spellView = frameCopyOnRead({}, new Map([[spellId, spell]]), { catalogs: 'share' }).members.get(spellId)!;
    janus(c, { casterId: ownerId, targetIds, abilityId: spellId, rawDiceMod: 0, rawDiceAdvDisStacks: 0,
      useAbilityVariable1: null, useAbilityVariable2: null, castingTargets: '' }, spellView, caster, targets);
  } finally { c.trigger = prior.trigger; c.engineFiredCast = prior.engine; c.statusConnectedId = prior.connection; }
}

/** Both combat boundaries and damage apply the same checked engine-fired cast. */
export function fireContingencyCast(c: JanusCtx, fire: ContingencyCast): void {
  applyContingencyCast(c, contingencyCastPlan(c, fire));
}

/** Damage is already written. Only newly reached severity conditions are due; a failed fire rolls back itself. */
export function fireDamageContingencies(ctx: JanusCtx, target: Row, before: number): void {
  const armed = activeStatuses(ctx, target).filter(st => CONTINGENCY_ARMING_IDS.includes(statusItemId(st)) && !intval(statusScalar(st, 'status_deleted') ?? 0));
  if (!armed.length) return;
  const after = Number(ecLean(target, 'v_current')), vitality = record(hydrateVitality(record(record(target.computed)?.vitality) ?? {}))!;
  const maximum = intval(vitality.effective_v_max ?? vitality.v_max), temporary = intval(vitality.v_temp ?? 0);
  if (maximum <= 0 || after >= before) return;
  const tiers = Object.entries(VITALITY_STATUS_IDS), severity = (current: number): number => tiers.findIndex(([, id]) => id === calculateVitalityStatus(current, maximum, temporary));
  const from = severity(before), to = severity(after);
  if (to <= from) return;
  ctx = journalBoundaryContext(ctx); target = boundaryValue(ctx, target);
  for (const st of armed) {
    const parsed = contingencyParse(statusScalar(st, 'status_casting_targets_arr'));
    if (!parsed.cond.startsWith('vstatus:')) continue;
    const tier = tiers.findIndex(([name]) => name === parsed.cond.slice(8));
    if (tier <= from || tier > to) continue;
    const statusId = upper(statusScalar(st, 'status_id')), ownerId = ecId(target), spellId = upper(statusScalar(st, 'status_origin_id'));
    if ((ctx.contingencyAttempts ??= new Set()).has(statusId)) continue;
    ctx.contingencyAttempts.add(statusId);
    if (!activeStatuses(ctx, target).some(row => upper(statusScalar(row, 'status_id')) === statusId)) continue;
    const restore = boundaryCheckpoint(ctx);
    try {
      if (!/^[0-9A-F-]{36}$/.test(statusId) || !/^[0-9A-F-]{36}$/.test(spellId) || isSelfStatusOrigin(statusId, spellId)) handBack('contingency-row-unreadable');
      const spell = ctx.read.definition(spellId);
      if (!spell) handBack('contingency:spell-not-held');
      const decision = contingencyManaPlan(parsed, spell, intval(ctx.read.value(spell, 'spell_level') ?? 0), intval(ecLean(target, 'mana') ?? 0), ecLean(target, 'mana_total'),
        { holder: target, read: resourceReductionReaders(ctx) });
      if (decision.mana && (!Number.isSafeInteger(decision.mana.after) || decision.mana.after < 0 || decision.mana.after > 2147483647)) handBack('contingency-mana-out-of-range');
      const packed = scalar(statusScalar(st, 'status_casting_targets_arr'));
      const stored = decision.kind === 'fire' && typeof packed === 'string' && packed.startsWith('{') ? record(JSON.parse(packed))?.t : null;
      const cast = decision.kind === 'fire' ? contingencyCastPlan(ctx,
        { ownerId, spellId, castLevel: decision.castLevel, targetIds: [stored ? upper(stored) : ownerId] }, spell) : null;
      // Claim before dispatch so a nested damage event cannot fire this same arm again.
      for (const row of [ctx.encounter, ...ctx.members.values()]) removeStatusFromRow(row, statusId);
      ctx.statusesRemoved.push({ status_id: statusId, status_item_id: statusItemId(st), target: ownerId });
      if (decision.mana) setMana(ctx, target, decision.mana.after);
      if (decision.kind === 'fizzle') {
        insertRoll(ctx, { roll_type_1: 'contingency_fizzled', roll_item_id: spellId, by: target, to: target, rolls_full: [[0, decision.reason]] });
        continue;
      }
      // A healing/defensive payload reads the damage which triggered it, including lean vitality defaults.
      const computed = record(target.computed)!;
      const liveVitality = record(hydrateVitality(record(computed.vitality) ?? {}))!;
      computed.vitality = { ...liveVitality, base_v_current: Number(ecLean(target, 'v_current')), v_current: Number(ecLean(target, 'v_current')),
        v_status: calculateVitalityStatus(Number(ecLean(target, 'v_current')), maximum, temporary) };
      applyContingencyCast(ctx, cast!);
      commitShapeGate(ctx, { assignStatusIds: false });
    } catch (error) {
      restore();
      if (!(error instanceof HandBack || error instanceof Refusal)) throw error;
      const reason = error instanceof HandBack ? error.reason : error.refusal;
      const name = String(ecLean(target, 'encounter_creature_name') ?? itemField(target, 'item_name') ?? 'Creature');
      ctx.rows.push({ ...unportedSkipRow(`server-contingency:${parsed.cond}:${reason} (${name})`), roll_by: ownerId, roll_to: ownerId });
    } finally { releaseRollback(restore); }
  }
}
